import React, { useState, useEffect, useCallback } from 'react';
import {
    getRaces, getUsers, getDrivers, getPicks, getQuestions,
    getRaceDrivers, adminSavePicks, mergeAccounts
} from '../../api';
import RaceSelector from '../../components/RaceSelector';
import DriverSelector from '../../components/DriverSelector';
import SuperAdminAI from './SuperAdminAI';
import { useAppAlert } from '../../components/AppAlerts';

export default function SuperAdmin() {
    // â”€â”€ shared data â”€â”€
    const [users, setUsers] = useState([]);
    const [message, setMessage] = useState('');
    const notify = useAppAlert();

    // â”€â”€ edit picks state â”€â”€
    const [editRace, setEditRace] = useState('');
    const [editUser, setEditUser] = useState('');
    const [positions, setPositions] = useState(Array(7).fill(null));
    const [dnf, setDnf] = useState(null);
    const [questions, setQuestions] = useState([]);
    const [bonusAnswers, setBonusAnswers] = useState({});
    const [raceDrivers, setRaceDrivers] = useState([]);
    const [driversMap, setDriversMap] = useState({});
    const [picksLoaded, setPicksLoaded] = useState(false);

    // â”€â”€ merge state â”€â”€
    const [parentId, setParentId] = useState('');
    const [childId, setChildId] = useState('');
    const [mergeMsg, setMergeMsg] = useState('');

    const showAlert = (text, typeOverride) => {
        if (!text) return;
        setMessage(text);
        const lower = String(text).toLowerCase();
        const resolvedType = typeOverride || (/error|unable|failed|invalid|select|provide|please|cannot|no user|missing/.test(lower) ? 'error' : 'success');
        notify(text, resolvedType);
    };

    const showMergeAlert = (text, typeOverride) => {
        if (!text) return;
        setMergeMsg(text);
        const lower = String(text).toLowerCase();
        const resolvedType = typeOverride || (/error|invalid|failed|select|cannot|missing/.test(lower) ? 'error' : 'success');
        notify(text, resolvedType);
    };

    // â”€â”€ load users on mount â”€â”€
    useEffect(() => {
        getUsers().then(setUsers);
    }, []);

    // â”€â”€ helpers â”€â”€
    const buildDriversMap = (list) => {
        const map = {};
        (list || []).forEach((d) => {
            map[d.id] = (d.name || `${d.first_name || ''} ${d.last_name || ''}`).trim();
        });
        return map;
    };

    const resetPicks = useCallback(() => {
        setPositions(Array(7).fill(null));
        setDnf(null);
        setBonusAnswers({});
        setQuestions([]);
        setRaceDrivers([]);
        setPicksLoaded(false);
    }, []);

    // â”€â”€ when race or user changes, load their picks â”€â”€
    useEffect(() => {
        if (!editRace || !editUser) { resetPicks(); return; }
        let cancelled = false;

        (async () => {
            try {
                const [driverList, qs, rdResp, pickData] = await Promise.all([
                    getDrivers(),
                    getQuestions(editRace),
                    getRaceDrivers(editRace),
                    getPicks(editRace, editUser),
                ]);
                if (cancelled) return;

                const rdList = rdResp?.drivers || [];
                if (rdList.length > 0) {
                    setRaceDrivers(rdList);
                    setDriversMap(buildDriversMap(rdList));
                } else {
                    setRaceDrivers(driverList);
                    setDriversMap(buildDriversMap(driverList));
                }
                setQuestions(qs || []);

                const me = (pickData || []).find((p) => String(p.username) === String(editUser));
                if (me) {
                    const normalized = me.picks.slice(3, 10).map((v) => v ?? null);
                    setPositions([
                        ...normalized,
                        ...Array(Math.max(0, 7 - normalized.length)).fill(null),
                    ]);
                    setDnf(me.dnf ?? null);
                    if (me.bonus_answers) {
                        const parsed = {};
                        me.bonus_answers.split(',').forEach((pair) => {
                            const [qId, ans] = pair.split(':');
                            if (qId && ans) parsed[qId] = parseInt(ans);
                        });
                        setBonusAnswers(parsed);
                    } else {
                        setBonusAnswers({});
                    }
                } else {
                    setPositions(Array(7).fill(null));
                    setDnf(null);
                    setBonusAnswers({});
                }
                setPicksLoaded(true);
            } catch (e) {
                console.error('SuperAdmin load error', e);
                showAlert('Error loading picks');
            }
        })();

        return () => { cancelled = true; };
    }, [editRace, editUser, resetPicks]);

    function updatePos(index, value) {
        setPositions((prev) => prev.map((v, i) => (i === index ? value : v)));
    }

    async function handleSavePicks() {
        if (!editRace || !editUser) return showAlert('Select a race and user first');

        const bonusStr = Object.entries(bonusAnswers)
            .filter(([, v]) => v != null)
            .map(([qId, ans]) => `${qId}:${ans}`)
            .join(',') || null;

        try {
            await adminSavePicks(editUser, [null, null, null, ...positions], editRace, dnf, bonusStr);
            showAlert('Picks saved successfully');
        } catch (err) {
            showAlert(err?.message || 'Failed to save picks');
        }
    }

    async function handleMerge() {
        if (!parentId || !childId) return showMergeAlert('Select both accounts');
        if (parentId === childId) return showMergeAlert('Cannot merge an account into itself');
        const parentUser = users.find((u) => String(u.id) === String(parentId));
        const childUser = users.find((u) => String(u.id) === String(childId));
        if (!window.confirm(
            `Merge "${childUser?.display_name || childUser?.username}" into "${parentUser?.display_name || parentUser?.username}"?\n\nThis will move the child's picks (where the parent has none) and DELETE the child account. This cannot be undone.`
        )) return;

        try {
            const result = await mergeAccounts(Number(parentId), Number(childId));
            showMergeAlert(result.message || 'Merge complete');
            setChildId('');
            // Refresh users list
            const refreshed = await getUsers();
            setUsers(refreshed);
        } catch (err) {
            showMergeAlert(err?.message || 'Merge failed');
        }
    }

    const userLabel = (u) => u.display_name || u.username;

    return (
        <div className="super-admin-page">
            <h3>Super Admin</h3>

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Edit Picks Section â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">Edit User Picks</h5>

                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label className="form-label">Race</label>
                            <RaceSelector value={editRace} onChange={(v) => setEditRace(v)} />
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">User</label>
                            <select className="form-select" value={editUser} onChange={(e) => setEditUser(e.target.value)}>
                                <option value="">-- select user --</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{userLabel(u)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    {picksLoaded && (
                        <>
                            <div className="form-group row">
                                {positions.map((val, i) => (
                                    <div key={`pos-${i}`} className="mb-3 row align-items-center">
                                        <label className="col-1 col-form-label">{`#${i + 4}`}</label>
                                        <div className="col-11">
                                            <DriverSelector
                                                value={val}
                                                onChange={(v) => updatePos(i, v)}
                                                drivers={raceDrivers.length > 0 ? raceDrivers : undefined}
                                            />
                                        </div>
                                    </div>
                                ))}
                                <div className="mb-3 row align-items-center">
                                    <label className="col-1 col-form-label">DNF</label>
                                    <div className="col-11">
                                        <DriverSelector
                                            value={dnf}
                                            onChange={(v) => setDnf(v)}
                                            includeNoDnf={true}
                                            drivers={raceDrivers.length > 0 ? raceDrivers : undefined}
                                        />
                                    </div>
                                </div>

                                {questions.length > 0 && (
                                    <>
                                        <hr />
                                        <label className="form-label fw-semibold">Bonus Questions</label>
                                        {questions.map((q, idx) => {
                                            const options = [
                                                q.option_1 ? { label: q.option_1, value: 1 } : null,
                                                q.option_2 ? { label: q.option_2, value: 2 } : null,
                                                q.option_3 ? { label: q.option_3, value: 3 } : null,
                                                q.option_4 ? { label: q.option_4, value: 4 } : null,
                                            ].filter(Boolean);
                                            const selected = bonusAnswers[String(q.id)];
                                            return (
                                                <div key={q.id} className="mb-3 row align-items-start">
                                                    <label className="col-1 col-form-label">Q{idx + 1}</label>
                                                    <div className="col-11">
                                                        <div className="mb-1">
                                                            <strong>{q.question_text}</strong>
                                                            <span className="badge bg-secondary ms-2">{q.points} pt{q.points !== 1 ? 's' : ''}</span>
                                                        </div>
                                                        <div className="d-flex flex-wrap gap-2">
                                                            {options.map((opt) => (
                                                                <div key={opt.value} className="form-check">
                                                                    <input
                                                                        className="form-check-input"
                                                                        type="radio"
                                                                        name={`sa-bonus-q-${q.id}`}
                                                                        id={`sa-bonus-q-${q.id}-opt-${opt.value}`}
                                                                        checked={selected === opt.value}
                                                                        onChange={() => setBonusAnswers((prev) => ({ ...prev, [String(q.id)]: opt.value }))}
                                                                    />
                                                                    <label className="form-check-label" htmlFor={`sa-bonus-q-${q.id}-opt-${opt.value}`}>
                                                                        {opt.label}
                                                                    </label>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </>
                                )}
                            </div>

                            <button className="btn btn-primary mt-2" type="button" onClick={handleSavePicks}>
                                Save Picks (Admin Override)
                            </button>
                        </>
                    )}
                </div>
            </div>

            {/* â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ Merge Accounts Section â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€ */}
            <div className="card mb-4">
                <div className="card-body">
                    <h5 className="card-title mb-3">Merge Accounts</h5>
                    <p className="small text-muted mb-3">
                        Move the child account's race picks into the parent account (only for races the parent has no picks),
                        then delete the child account. This cannot be undone.
                    </p>

                    <div className="row g-3 mb-3">
                        <div className="col-md-6">
                            <label className="form-label">Parent Account (keep)</label>
                            <select className="form-select" value={parentId} onChange={(e) => setParentId(e.target.value)}>
                                <option value="">-- select parent --</option>
                                {users.map((u) => (
                                    <option key={u.id} value={u.id}>{userLabel(u)}</option>
                                ))}
                            </select>
                        </div>
                        <div className="col-md-6">
                            <label className="form-label">Child Account (merge &amp; delete)</label>
                            <select className="form-select" value={childId} onChange={(e) => setChildId(e.target.value)}>
                                <option value="">-- select child --</option>
                                {users.filter((u) => String(u.id) !== String(parentId)).map((u) => (
                                    <option key={u.id} value={u.id}>{userLabel(u)}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <button className="btn btn-danger" type="button" onClick={handleMerge} disabled={!parentId || !childId}>
                        Merge Accounts
                    </button>

                    {mergeMsg && <div className="mt-2 text-success">{mergeMsg}</div>}
                </div>
            </div>

            <SuperAdminAI />
        </div>
    );
}

